// src/features/ride-share/ui/ride-share-screen.tsx
import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@src/ui/views/card";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@src/ui/views/table";
import { Avatar, AvatarImage, AvatarFallback } from "@src/ui/views/avatar";
import { Badge } from "@src/ui/views/badge";
import { Button } from "@src/ui/views/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@src/ui/views/select";
import {
  Car,
  Users,
  Ruler,
  Activity,
  Clock,
  Download,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@src/ui/views/dropdown-menu";
import {
  downloadCsv,
  downloadXlsx,
  type TableColumn,
} from "@src/utils/table-export";
import {
  DateRangePicker,
  type DateRangeValue,
} from "@src/features/ride-share/ui/components/date-range-picker";
import {
  useGetRideSummaryQuery,
  useGetRecentRidesQuery,
} from "@src/features/ride-share/redux/ride-share-api";
import type { RideSummary, RecentRide } from "@src/features/ride-share/ride-share-models";

/** "Sun, 09 Aug 2026 00:00:00 GMT" → "2026-08-09" for spreadsheet-friendly dates. */
const toIsoDate = (value: string | null): string => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toISOString().slice(0, 10);
};

/** Columns written to the exported file, in order. */
const RIDE_EXPORT_COLUMNS: TableColumn<RecentRide>[] = [
  { header: "Ride ID", value: (r) => r.rideId },
  { header: "Driver", value: (r) => r.driverName },
  { header: "Status", value: (r) => r.rideStatus },
  { header: "Type", value: (r) => r.rideType },
  { header: "Date", value: (r) => toIsoDate(r.rideDate) },
  {
    header: "Time",
    value: (r) =>
      `${String(r.hours).padStart(2, "0")}:${String(r.minutes).padStart(2, "0")}`,
  },
  { header: "Participant Count", value: (r) => r.participants.length },
  {
    header: "Participants",
    value: (r) =>
      r.participants
        .map((p) => `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim())
        .filter(Boolean)
        .join(", "),
  },
];

type FilterMode = "count" | "range";

export function RideShareScreen() {
  const { spaceId } = useParams<{ spaceId: string }>();

  const [mode, setMode] = React.useState<FilterMode>("count");
  const [recentCount, setRecentCount] = React.useState<number>(20);
  const [range, setRange] = React.useState<DateRangeValue>({});

  // A range filter only makes sense once at least one bound is chosen;
  // until then keep showing the last-N result.
  const rangeActive = mode === "range" && Boolean(range.startDate || range.endDate);

  // summary cards — always the trailing 30 days, independent of the filter below
  const { data: sumRes, isLoading: sumLoading, isError: sumError } =
    useGetRideSummaryQuery({ spaceId: spaceId! });

  // rides table (with participants)
  const {
    data: recentRes,
    isLoading: recLoading,
    isFetching: recFetching,
    isError: recError,
  } = useGetRecentRidesQuery(
    rangeActive
      ? { spaceId: spaceId!, startDate: range.startDate, endDate: range.endDate }
      : { spaceId: spaceId!, limit: recentCount },
  );

  const [selectedRide, setSelectedRide] = React.useState<RecentRide | null>(null);

  if (sumLoading || recLoading) return <p>Loading…</p>;
  if (sumError || recError || !sumRes || !recentRes) return <p>Error loading data.</p>;

  const summary: RideSummary = sumRes.data;
  const recent: RecentRide[] = recentRes.data;
  // Only meaningful for a date range — "last N" returning exactly N is expected.
  const hitRowCap = rangeActive && (recentRes.meta?.truncated ?? false);

  // helper to format "YYYY-MM-DD" → "Feb 12"
  const formatShortDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  // helper to format H and M
  const formatTime = (h: number, m: number) =>
    `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

  // export the rides currently displayed in the table
  const exportFileName = rangeActive
    ? `ride-share-${range.startDate ?? "start"}-to-${range.endDate ?? "today"}`
    : `ride-share-last-${recentCount}-${new Date().toISOString().slice(0, 10)}`;

  const handleDownloadCsv = () =>
    downloadCsv(exportFileName, recent, RIDE_EXPORT_COLUMNS);

  const handleDownloadXlsx = () =>
    downloadXlsx(exportFileName, recent, RIDE_EXPORT_COLUMNS, "Recent Rides");

  return (
    <div className="space-y-8 p-6 bg-white rounded-lg shadow">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Total Rides</CardTitle>
            <Car className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-3xl font-bold">
            {summary.total_rides}
            <p className="text-xs text-muted-foreground">
            {`${summary.pct_change_rides >= 0 ? '+' : ''}${summary.pct_change_rides.toFixed(1)}% from previous 30 days`}
						</p>
  
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Avg. Passengers</CardTitle>
            <Users className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-3xl font-bold">
            {summary.avg_passengers.toFixed(2)}
            <p className="text-xs text-muted-foreground">
            {`${summary.pct_change_participants >= 0 ? '+' : ''}${summary.pct_change_participants.toFixed(1)}% from previous 30 days`}
						</p>
          </CardContent>
        </Card>


        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Total Riders</CardTitle>
            <Activity className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent className="text-3xl font-bold">{summary.total_riders}
          <p className="text-xs text-muted-foreground">
          {`${summary.total_riders >= summary.total_prev_riders ? '+' : ''}${summary.total_prev_riders > 0 ?  (((summary.total_riders - summary.total_prev_riders)/summary.total_prev_riders) * 100).toFixed(1) : 0}% from previous 30 days`}
						</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-4">
        {/* Mode switch: last N rides, or an explicit date range. */}
        <div className="flex rounded-md border p-0.5">
          {(
            [
              ["count", "Last rides"],
              ["range", "Date range"],
            ] as const
          ).map(([value, text]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={
                "rounded-sm px-3 py-1 text-sm transition-colors " +
                (mode === value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground")
              }
            >
              {text}
            </button>
          ))}
        </div>

        {mode === "count" ? (
          <label className="flex items-center space-x-2 text-sm text-muted-foreground">
            <span>Show last</span>
            <Select
              value={String(recentCount)}
              onValueChange={(v) => setRecentCount(Number(v))}
            >
              <SelectTrigger className="w-20">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 50, 100, 300].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>rides</span>
          </label>
        ) : (
          <div className="flex items-center gap-2">
            <DateRangePicker value={range} onChange={setRange} />
            {(range.startDate || range.endDate) && (
              <Button variant="ghost" size="sm" onClick={() => setRange({})}>
                Clear
              </Button>
            )}
          </div>
        )}

        {/* Primary action downloads CSV; Excel is available in the menu. */}
        <div className="flex items-center">
          <Button
            variant="outline"
            size="sm"
            className="rounded-r-none"
            onClick={handleDownloadCsv}
            disabled={recent.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Download CSV
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-l-none border-l-0 px-2"
                disabled={recent.length === 0}
                aria-label="More download formats"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={handleDownloadCsv}>
                CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleDownloadXlsx}>
                Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>


      {/* Recent Rides Table */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold">
            {rangeActive ? "Rides" : "Recent Rides"}
          </h2>
          <span className="flex items-center text-sm text-muted-foreground">
            <Clock className="h-4 w-4 mr-1" />
            {recFetching
              ? "Loading…"
              : rangeActive
                ? `${recent.length} ride${recent.length === 1 ? "" : "s"}`
                : `Last ${recentCount}`}
          </span>
        </div>

        {hitRowCap && (
          <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
            This range has more rides than can be loaded at once. Showing the{" "}
            {recent.length.toLocaleString()} most recent — narrow the dates to see
            the rest. Exports contain only the rides shown here.
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="text-right">Participants</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {recent.map((ride) => (
              <TableRow
                key={ride.rideId}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => setSelectedRide(ride)}
              >
                {/* Driver avatar + name */}
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={ride.driverProfilePic ?? ""} alt={ride.driverName} />
                      <AvatarFallback>
                        {ride.driverName?.[0] ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <span>{ride.driverName}</span>
                  </div>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {ride.rideStatus ?? "—"}
                  </Badge>
                </TableCell>

                {/* Type */}
                <TableCell>{ride.rideType}</TableCell>

                {/* Short date */}
                <TableCell>{formatShortDate(ride.rideDate)}</TableCell>

                {/* Time */}
                <TableCell>{formatTime(ride.hours, ride.minutes)}</TableCell>

                {/* Participants */}
                <TableCell className="text-right">
                  <div className="flex justify-end -space-x-2">
                    {ride.participants.map((p) => (
                      <Avatar
                        key={p.partId}
                        className="h-6 w-6 rounded-full border-2 border-white"
                      >
                        <AvatarImage src={p.profilePic ?? ""} alt={p.firstName} />
                        <AvatarFallback>
                          {p.firstName?.[0] ?? "?"}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Participants Modal */}
      {selectedRide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-96 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">
              Participants for ride {selectedRide.rideId}
            </h3>

            <div className="space-y-3">
              {selectedRide.participants.length > 0 ? (
                selectedRide.participants.map((p) => (
                  <div
                    key={p.partId}
                    className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={p.profilePic ?? ""} alt={p.firstName} />
                      <AvatarFallback>
                        {p.firstName?.[0] ?? "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">
                        {p.firstName} {p.lastName}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {p.participantNotes ?? p.status ?? ""}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No participants</p>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={() => setSelectedRide(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
